// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { constants } from "@bg/XDL/constants";
import { hasLocalStorage, parseBoolean } from "@bg/XDL/Utilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import displayProducts, {
    chain,
    GetUnfilteredProducts,
    LoadRealTimePricingAndInventory,
    PopulateApiParameter,
    RemoveNotAvailableAttributeValues,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import loadProductsPricingAndInventory from "@insite/client-framework/Store/Pages/ProductList/Handlers/LoadProductsPricingAndInventory";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import qs from "qs";

type PropsType = Parameters<typeof PopulateApiParameter>[0];
type ApiParameterType = PropsType["apiParameter"];

type XngApiParameter = {
    includeProductsInSubCategories?: boolean;
} & ApiParameterType;

export const XngPopulateApiParameter: typeof PopulateApiParameter = props => {
    const filters = props.result.productFilters;
    props.apiParameter = {
        ...props.apiParameter,
        includeProductsInSubCategories: !!(filters.categoryId?.length || filters.pageCategoryId?.length),
    } as XngApiParameter;
};

// Don't delete the categoryId in case we have included products for subCategories to fetch all the categoryFacets with the call
export const XngGetUnfilteredProducts: typeof GetUnfilteredProducts = async props => {
    const unfilteredApiParameter = { ...props.apiParameter } as XngApiParameter;

    delete unfilteredApiParameter.searchWithin;
    if (!props.result.productFilters.pageBrandId) {
        delete unfilteredApiParameter.brandIds;
    }
    if (!props.result.productFilters.pageProductLineId) {
        delete unfilteredApiParameter.productLineIds;
    }
    delete unfilteredApiParameter.priceFilters;
    delete unfilteredApiParameter.attributeValueIds;
    // XNG-Changes: Don't delete the categoryId in case we have include products for subCategories to fetch all the categoryFacets with the call
    if (!props.result.productFilters.pageCategoryId && !unfilteredApiParameter.includeProductsInSubCategories) {
        delete unfilteredApiParameter.categoryId;
    }
    unfilteredApiParameter.stockedItemsOnly = undefined;
    unfilteredApiParameter.previouslyPurchasedProducts = undefined;

    props.result.unfilteredApiParameter = unfilteredApiParameter;
    props.result.filteredApiParameter = { ...props.apiParameter };

    const unfilteredProducts = getProductsDataView(props.getState(), unfilteredApiParameter).value;
    if (unfilteredProducts) {
        return;
    }

    await executeAwaitableHandlerChain(loadProducts, unfilteredApiParameter, props);
};

export const XngLoadRealTimePricingAndInventory: typeof LoadRealTimePricingAndInventory = props => {
    if (
        enablePostalCodeLocationFinder(props.getState()) &&
        !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) &&
        !props.getState().context.session.isAuthenticated
    ) {
        return;
    }

    props.dispatch(
        loadProductsPricingAndInventory({
            products: props.result.products,
            productInfosByProductId: props.result.productInfosByProductId,
        }),
    );
};

export const XngRemoveNotAvailableAttributeValues: typeof RemoveNotAvailableAttributeValues = props => {
    // XNG-Change: get headerShopByCatLinks
    const state = props.getState();
    const headerShopByCatLinks =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "ShopByCategoryL1L2/ShopByCategoryL1L2";
        })?.fields.l1Links as any[]) ?? [];

    const productsDataView = getProductsDataView(props.getState(), props.apiParameter);
    if (!productsDataView.value) {
        return;
    }

    const possibleAttributeValueIds = new Set();
    productsDataView?.attributeTypeFacets?.forEach(a =>
        a.attributeValueFacets?.forEach(v => possibleAttributeValueIds.add(v.attributeValueId)),
    );

    // XNG-Change: add custom attributes to possibleAttributeValueIds
    headerShopByCatLinks.length &&
        headerShopByCatLinks.forEach(catLink => {
            const { type, value } = catLink.fields.destination;
            if (type?.toLowerCase() === "url" && value?.includes("attributeValueIds=")) {
                possibleAttributeValueIds.add(value.slice(value.indexOf("attributeValueIds=") + 18));
            }
        });

    const removedAttributeValueIds = props.result.productFilters.attributeValueIds?.filter(
        o => !possibleAttributeValueIds.has(o),
    );
    if (
        removedAttributeValueIds?.length &&
        !props.result.productFilters.stockedItemsOnly &&
        !props.result.productFilters.previouslyPurchasedProducts
    ) {
        const attributeValueIds = props.result.productFilters.attributeValueIds?.filter(o =>
            possibleAttributeValueIds.has(o),
        );
        props.dispatch(removeProductFilters({ attributeValueIds: removedAttributeValueIds }));
        const parsedQuery = parseQueryString<any>(props.parameter.queryString);
        parsedQuery.attributeValueIds = attributeValueIds?.length ? attributeValueIds.join(",") : undefined;
        const parameter = { ...props.parameter, queryString: qs.stringify(parsedQuery) };
        props.dispatch(displayProducts(parameter));
        return false;
    }
};

addToChainAfter(chain, PopulateApiParameter, XngPopulateApiParameter);
replaceInChain(chain, RemoveNotAvailableAttributeValues, XngRemoveNotAvailableAttributeValues);
replaceInChain(chain, GetUnfilteredProducts, XngGetUnfilteredProducts);
replaceInChain(chain, LoadRealTimePricingAndInventory, XngLoadRealTimePricingAndInventory);
